import QRCode from 'qrcode';

/**
 * Synchronous, deterministic QR → SVG-rects renderer.
 *
 * `QRCode.create()` is synchronous and pure, so this is SSR-safe: a given
 * `data` string produces byte-identical markup for every visitor and for
 * Googlebot (no async, no per-request state). That preserves the CTA
 * module's no-cloaking invariant.
 *
 * Returns the module matrix as `{ size, cells }` where `cells` is the list
 * of dark-module coordinates — the caller emits `<rect>`s. Error-correction
 * level M tolerates the navy-overlay print context while keeping the matrix
 * small enough to scan at ~132px.
 */
export interface QrMatrix {
  size: number;
  cells: Array<{ x: number; y: number }>;
}

export function qrMatrix(data: string): QrMatrix {
  const qr = QRCode.create(data, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const cells: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (qr.modules.get(x, y)) cells.push({ x, y });
    }
  }
  return { size, cells };
}

/**
 * The beta destination encoded by every CTA QR. Unlike the click-through
 * button (which intentionally carries NO utm — same-site nav would reset the
 * GA4 session), a QR scan starts a fresh session on a DIFFERENT device, so
 * utm is the only way to attribute it; without it the scan lands as
 * "(direct)". `GetAppPage` already forwards utm_* into join-ios-beta →
 * beta_waitlist, so these params reach the conversion row.
 */
export const CTA_QR_URL =
  'https://www.aviado.com/get-app?utm_source=research&utm_medium=qr&utm_campaign=beta';
